// Путь: D:\QR\QR-WEB\dashboard\src\components\Layout\BusinessLayout.jsx
// Название: Business Layout v5.0 - Brand Lead Generation
// Версия: 5.0.1
// Дата: 22.01.2026

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './BusinessLayout.css';

const BusinessLayout = ({ children, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest('.user-menu-wrapper')) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isUserMenuOpen]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const handleNavigate = (path) => {
    setIsUserMenuOpen(false);
    navigate(path);
  };

  const handleLogoutClick = () => {
    setIsUserMenuOpen(false);
    handleLogout();
  };

  // ============================================
  // МЕНЮ НАВИГАЦИИ v5.0 - Brand Lead Generation
  // ============================================
  const menuSections = [
    {
      title: '🏠 ГЛАВНАЯ',
      items: [
        { path: '/business/dashboard', label: 'Дашборд', icon: '📊' }
      ]
    },
    {
      title: '🎯 КАМПАНИИ',
      items: [
        { path: '/business/brand/create', label: 'Создать кампанию', icon: '➕' },
        { path: '/business/brand/campaigns', label: 'Все кампании', icon: '📂' },
        { path: '/business/brand/analytics', label: 'Аналитика', icon: '📈' }
      ]
    },
    {
      title: '👥 ЛИДЫ',
      items: [
        { path: '/business/leads', label: 'Все лиды', icon: '🎯' }
      ]
    },
    {
      title: '🎟️ ПРОМОКОДЫ',
      items: [
        { path: '/business/promocodes', label: 'Управление', icon: '🎫' }
      ]
    },
    {
      title: '💰 ФИНАНСЫ',
      items: [
        { path: '/business/billing', label: 'Подписка', icon: '💳' },
		{ path: '/business/wallet', label: 'Кошелёк', icon: '💰' }
      ]
    },
    {
      title: '⚙️ НАСТРОЙКИ',
      items: [
        { path: '/business/profile', label: 'Профиль компании', icon: '🏢' }
      ]
    }
  ];

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getInitials = (email) => {
    return email?.split('@')[0].substring(0, 2).toUpperCase() || 'BZ';
  };

  return (
    <div className="business-layout">
      {/* SIDEBAR */}
      <div className={`sidebar ${isMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <span className="logo-icon">🎁</span>
            <div className="logo-text">
              <h2>DARY</h2>
              <span className="logo-tagline">Brand Lead Generation</span>
            </div>
          </div>
          <button
            className="menu-toggle-close"
            onClick={() => setIsMenuOpen(false)}
            aria-label="Закрыть меню"
          >
            ✕
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="nav-section">
              <div className="nav-section-title">{section.title}</div>
              <div className="nav-section-items">
                {section.items.map((item) => (
                  <button
                    key={item.path}
                    className={`nav-item ${isActiveRoute(item.path) ? 'active' : ''}`}
                    onClick={() => {
                      navigate(item.path);
                      setIsMenuOpen(false);
                    }}
                    aria-current={isActiveRoute(item.path) ? 'page' : undefined}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            🚪 Выход
          </button>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="main-content">
        <header className="top-bar">
          <div className="top-bar-left">
            <button
              className="menu-toggle"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Открыть меню"
            >
              ☰
            </button>
            <div className="top-bar-title">
              <h1>🏢 Business Panel</h1>
            </div>
          </div>

          <div className="top-bar-right">
            <div className="user-menu-wrapper">
              <button 
                className="user-avatar-btn"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              >
                <div className="user-avatar">{getInitials(user?.email)}</div>
              </button>
              
              {isUserMenuOpen && (
                <div className="user-dropdown">
                  <div className="user-info">
                    <div className="user-avatar-large">{getInitials(user?.email)}</div>
                    <div className="user-details">
                      <p className="user-email">{user?.email || 'Business'}</p>
                      <p className="user-role">BUSINESS ACCOUNT</p>
                    </div>
                  </div>
                  <div className="user-menu-divider"></div>
                  <button 
                    className="user-menu-item"
                    onClick={() => handleNavigate('/business/profile')}
                  >
                    ⚙️ Настройки
                  </button>
                  <div className="user-menu-divider"></div>
                  <button 
                    className="user-menu-item logout"
                    onClick={handleLogoutClick}
                  >
                    🚪 Выход
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="content-area">
          {children}
          <footer className="layout-footer">
            <div className="footer-content">
              <div className="footer-left">
                <span className="footer-logo">🎁 DARY</span>
                <p className="footer-desc">Brand Lead Generation Platform</p>
              </div>
              <div className="footer-right">
                <p>© 2026 DARY Business</p>
              </div>
            </div>
          </footer>
        </div>
      </div>

      {isMenuOpen && (
        <div 
          className="overlay" 
          onClick={() => setIsMenuOpen(false)}
          aria-hidden="true"
        ></div>
      )}
    </div>
  );
};

export default BusinessLayout;